import { type FC, useEffect } from 'react';
import cn from 'classnames';
import { motion, type Variants } from 'framer-motion';

import { playSound } from '@/common/lib/soundEffect';
import { selectMainAndMiniGameTileList } from '@/redux/selectors/diceGameSelectors';
import { useAppSelector } from '@/redux/store';

import { TileSelector } from '../Tiles/TileSelector/TileSelector';

import css from './styles.module.scss';

interface TilesBoardProps {
  boardPosition: number;
}

const TilesBoard: FC<TilesBoardProps> = ({ boardPosition }) => {
  const activeBoard = useAppSelector((state) => state.diceGame.activeBoard);
  const mainAndMiniGameTileList = useAppSelector(selectMainAndMiniGameTileList);
  const skipAnimation = useAppSelector((state) => state.diceGame.skipAnimation);
  const boardKind = useAppSelector((state) => state.diceGame.miniGame?.type) as
    | 'tgem'
    | 'str'
    | undefined;

  useEffect(() => {
    if (activeBoard === 'miniGame' && boardKind === 'str')
      playSound('win/bright');
  }, [activeBoard, boardKind]);

  const animateToMiniGameTiles: Variants = {
    animate: (index: number) => ({
      rotateY: '180deg',
      transition: {
        duration: skipAnimation ? 0 : 0.3,
        delay: skipAnimation ? 0 : index * 0.1 + 0.1,
      },
    }),
  };

  const animateToMainGameTiles: Variants = {
    animate: (index: number) => ({
      rotateY: '0deg',
      transition: {
        duration: skipAnimation ? 0 : 0.3,
        delay: skipAnimation ? 0 : index * 0.1 + 0.4,
      },
    }),
  };

  return (
    <>
      {mainAndMiniGameTileList.map(
        ({ mainGameTileData, miniGameTileData }, index) => (
          <div
            key={`${mainGameTileData}-${index}`}
            style={{ gridArea: 'a' + (index + 1) }}
            className={css.card}
          >
            <motion.div
              custom={index}
              variants={
                activeBoard === 'miniGame'
                  ? animateToMiniGameTiles
                  : animateToMainGameTiles
              }
              animate="animate"
              className={css.cardInner}
            >
              <TileSelector
                kind={mainGameTileData}
                props={{
                  isActive:
                    boardPosition % 20 === index && activeBoard !== 'miniGame',
                  className: cn('absolute', css.cardFront, css.sidePosition),
                }}
              />
              {mainGameTileData !== 'tgem' ||
                (activeBoard !== 'miniGame' && (
                  <div className={css.cardCore}></div>
                ))}
              {miniGameTileData !== undefined && boardKind && (
                <TileSelector
                  kind={boardKind}
                  props={{
                    isSuperGame: activeBoard === 'miniGame',
                    value: miniGameTileData as number,
                    isActive: boardPosition % 20 === index,
                    variant: index ? 'mini-game' : 'start',
                    className: cn(css.sidePosition, css.cardBack),
                  }}
                />
              )}
            </motion.div>
          </div>
        ),
      )}
    </>
  );
};

export { TilesBoard };
